import type { KeyboardEvent, ReactNode } from 'react';
import styled from 'styled-components';
import { useIsMobile } from '../../../hooks/useIsMobile';
import type { BoxProps } from '../../Core';
import { Tooltip, type TooltipProps } from '../../Tooltip';
import { Label } from '../styles';
import { ToggleSwitch, ToggleSwitchWrapper, ToggleWrapper, type ToggleWrapperProps } from './styles';

export * from './styles';

/**
 * Toggle component - A visual switch that can be toggled on or off (alternative to a simple checkbox)
 *
 */
export const Toggle = styled(
  ({
    label,
    checked,
    disabled,
    tooltip,
    onChange,
    tooltipProps,
    fullWidth,
    switchOnly,
    onKeyDown,
    tabIndex = 0,
    ...props
  }: ToggleProps) => {
    const isMobile = useIsMobile();
    const showTooltipUnderline = isMobile || !!tooltipProps?.showUnderline;
    const toggle = () => {
      if (!disabled) {
        onChange(!checked);
      }
    };
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(event);
      if (event.defaultPrevented || disabled || (event.key !== ' ' && event.key !== 'Enter')) {
        return;
      }
      event.preventDefault();
      toggle();
    };

    return (
      <ToggleWrapper
        data-testid="toggle-button"
        data-checked={checked}
        data-disabled={disabled}
        onClick={switchOnly ? undefined : toggle}
        disabled={disabled}
        checked={checked}
        fullWidth={fullWidth}
        $switchOnly={switchOnly}
        {...props}
        role="switch"
        aria-checked={checked}
        aria-disabled={disabled || undefined}
        tabIndex={disabled ? -1 : tabIndex}
        onKeyDown={handleKeyDown}
      >
        {label && (
          <>
            {tooltip ? (
              <Tooltip {...tooltipProps} tooltip={tooltip} showUnderline={showTooltipUnderline}>
                <Label tooltip={!showTooltipUnderline}>{label}</Label>
              </Tooltip>
            ) : (
              <Label>{label}</Label>
            )}
          </>
        )}
        <ToggleSwitchWrapper
          onClick={
            switchOnly
              ? event => {
                  event.stopPropagation();
                  toggle();
                }
              : undefined
          }
        >
          <ToggleSwitch data-testid="toggle-switch" />
        </ToggleSwitchWrapper>
      </ToggleWrapper>
    );
  }
)``;

export type ToggleProps = Omit<ToggleWrapperProps, '$switchOnly'> &
  Omit<BoxProps, keyof ToggleWrapperProps | 'onClick' | 'onChange'> & {
    /** Tooltip text/component to show */
    tooltip?: ReactNode;
    /** Extra props from {@link TooltipProps} - the other {@link tooltip} prop takes the priority for specified display, if provided */
    tooltipProps?: Omit<TooltipProps, 'children' | 'tooltip'>;
    /** Expand the toggle to fill its container and make the whole row clickable. */
    fullWidth?: boolean;
    /** Only the switch toggles. The label stays free for its tooltip. */
    switchOnly?: boolean;

    onChange(value: boolean): void;
  };
